// externals
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// libraries
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import * as insuranceActionCreators from '@makemydeal/dr-dash-store/actions/insuranceActionsCreators';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import * as customerRiskAndCoverageActionCreators from '@makemydeal/dr-dash-store/actions/customerRiskAndCoverageActionCreators';
import * as contractingSelectors from '@makemydeal/dr-dash-store/selectors/contractingSelectors';
import * as contractingActionCreators from '@makemydeal/dr-dash-store/actions/contractingActionCreators';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';

// types
import type { VehicleInsurance } from '@makemydeal/dr-platform-types';

// shared form + props types
import { InsuranceForm, LoadingSection, DiscardChangesModal } from '@makemydeal/dr-shared-components';
import type { InsuranceFormRef } from '@makemydeal/dr-shared-components';
import { createInsuranceAnalyticsHandler } from './insuranceAnalyticsHandler';

// components
import { Button } from '@interstate/components/Button';
import { Alert } from '@interstate/components/Alert';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';

// local
import { useAssetsData } from '../dealActivity/activities/AssetsProvider';
import { dealLockHooks } from '../hooks';

// styles
import { InsuranceFooterContainer } from './CustomerRiskAndCoverageSlideOut.style';

/**
 * External ref exposed by the container — backward-compatible with old InsuranceFormRef.
 */
export type InsuranceFormContainerRef = {
    cancel: () => void;
    apply: () => void | boolean | Promise<void | boolean>;
};

export type InsuranceFormContainerProps = {
    embedded?: boolean;
    onValidationChange?: (isValid: boolean, formChanged: boolean) => void;
    onContractingErrorsAlert?: (hasErrors: boolean) => void;
};

const InsuranceFormContainer = forwardRef<InsuranceFormContainerRef, InsuranceFormContainerProps>(
    ({ embedded, onValidationChange, onContractingErrorsAlert }, ref) => {
        const dispatch = useDispatch();

        const vehicleInsuranceInfo = useSelector(baseDealSelectors.getVehicleInsuranceInfo);
        const isVehicleInsuranceUpdated = useSelector(baseDealSelectors.isVehicleInsuranceUpdated) ?? false;
        const sessionId = useSelector(offerReduxSelectors.getSessionId);
        const dealXgId = useSelector(offerReduxSelectors.getDealXgId);
        const dealXgVersion = useSelector(offerReduxSelectors.getDealXgVersion);
        const dealerId = useSelector(dealerSelectors.getDealerId);
        const vin = useSelector(vehicleSelectors.getVehicleVin);
        const buyerInfo = useSelector(offerReduxSelectors.getShopperInfo);

        const isAdditionalInsuranceFieldsEnabled = useSelector(featureToggleSelectors.isAdditionalInsuranceFieldsEnabled);

        const isLocked = dealLockHooks.useIsDealLocked();
        const isContractingContext = useSelector(contractingSelectors.getIsContractingContext);
        const contractApiErroredVehicleInsurance = useSelector(contractingSelectors.getContractApiErroredVehicleInsurance);

        const { getInsuranceProviders, insuranceProviders } = useAssetsData();

        const formRef = useRef<InsuranceFormRef>(null);

        const [updateStatus, setUpdateStatus] = useState<'ready' | 'sending' | 'success' | 'failure'>('ready');
        const [updateError, setUpdateError] = useState<string | null>(null);
        const [showDiscardModal, setShowDiscardModal] = useState(false);
        const [isFormValid, setIsFormValid] = useState(true);

        const theme = useInterstateTheme();

        const logContext = useMemo<Record<string, string | number | boolean>>(
            () => ({
                ...(sessionId !== undefined && { sessionId }),
                dealXgId,
                dealXgVersion,
                dealerId,
                vin
            }),
            [sessionId, dealXgId, dealXgVersion, dealerId, vin]
        );

        const logNRAction = useCallback(
            (attributes: Record<string, string | boolean>) => {
                logNewRelicPageAction('MV:InsuranceForm', { ...logContext, ...attributes });
            },
            [logContext]
        );

        const handleSave = useCallback(
            async (payload: VehicleInsurance): Promise<boolean> => {
                setUpdateStatus('sending');
                setUpdateError(null);

                try {
                    const result = await (dispatch(
                        baseDealActionCreators.updateVehicleInsuranceInfo(payload, dealXgId) as any
                    ) as Promise<{ success: boolean; message?: string }>);

                    if (result.success) {
                        setUpdateStatus('success');
                        logNRAction({ event: 'Save', status: 'success' });

                        dispatch(baseDealActionCreators.saveVehicleInsuranceInfo(payload));

                        if (isContractingContext) {
                            dispatch(contractingActionCreators.refreshContractValidationErrors());
                        }

                        setTimeout(() => {
                            if (!embedded) {
                                dispatch(baseDealActionCreators.setCustomerRiskAndCoverageSlideOutStatus(false));
                            }
                            setUpdateStatus('ready');
                        }, 1500);
                        return true;
                    } else {
                        setUpdateStatus('failure');
                        const errorMessage = result.message || 'There was a problem updating. Please try again.';
                        setUpdateError(errorMessage);
                        logNRAction({ event: 'Save', status: 'failure', updateError: errorMessage });
                        setTimeout(() => {
                            setUpdateStatus('ready');
                        }, 2000);
                        return false;
                    }
                } catch (error: any) {
                    setUpdateStatus('failure');
                    const errorMessage = error && error.message ? error.message : 'There was a problem updating. Please try again.';
                    setUpdateError(errorMessage);
                    logNRAction({ event: 'Save', status: 'failure', updateError: errorMessage });
                    setTimeout(() => {
                        setUpdateStatus('ready');
                    }, 2000);
                    return false;
                }
            },
            [dispatch, dealXgId, isContractingContext, embedded, logNRAction]
        );

        const closeInsuranceSlideout = useCallback(() => {
            if (!embedded) {
                dispatch(baseDealActionCreators.setCustomerRiskAndCoverageSlideOutStatus(false));
            }
            dispatch(customerRiskAndCoverageActionCreators.closeCustomerRiskAndCoverageSlideOut());
            dispatch(insuranceActionCreators.cancelInsuranceProvider());
        }, [dispatch, embedded]);

        const handleCancelClick = useCallback(() => {
            const isDirty = formRef.current?.isDirty() ?? false;
            if (isDirty) {
                setShowDiscardModal(true);
            } else {
                closeInsuranceSlideout();
            }
        }, [closeInsuranceSlideout]);

        const handleDiscardChanges = useCallback(() => {
            setShowDiscardModal(false);
            closeInsuranceSlideout();
        }, [closeInsuranceSlideout]);

        const handleGoBack = useCallback(() => {
            setShowDiscardModal(false);
        }, []);

        const applyInsurance = useCallback(async (): Promise<boolean> => {
            const isValid = await formRef.current?.validate();
            if (!isValid) return false;

            const payload = formRef.current!.getValues();
            const ok = await handleSave(payload);

            dispatch(insuranceActionCreators.saveInsuranceProvider());

            return ok;
        }, [handleSave, dispatch]);

        useImperativeHandle(ref, () => ({
            cancel: handleCancelClick,
            apply: applyInsurance
        }));

        const handleFormChange = useCallback(
            (isDirty: boolean, isValid: boolean) => {
                setIsFormValid(isValid);
                onValidationChange?.(isValid, isDirty);
            },
            [onValidationChange]
        );

        const handleProviderInteraction = useMemo(() => createInsuranceAnalyticsHandler(dispatch), [dispatch]);

        const onSetErrorFields = useCallback(
            (entity: string, errorFlags: Record<string, boolean>) => {
                dispatch(dealScreenErrorActionCreators.setErrorFields(entity, errorFlags));
            },
            [dispatch]
        );

        const slideOutFooterActions = (
            <InsuranceFooterContainer theme={theme}>
                {updateError && (
                    <Alert type="error" sx={{ marginBottom: '16px' }} data-testid="vehicle-insurance-error-message">
                        {updateError}
                    </Alert>
                )}
                <Button
                    data-testid="vehicle-insurance-slideout-cancel-btn"
                    id="vehicle-insurance-slideout-cancel-btn"
                    buttonStyle="tertiary"
                    onClick={handleCancelClick}
                    disabled={updateStatus === 'sending' || updateStatus === 'success'}
                >
                    Close
                </Button>
                <Button
                    disabled={!isFormValid || isLocked}
                    data-testid="vehicle-insurance-slideout-save-btn"
                    id="vehicle-insurance-slideout-save-btn"
                    buttonStyle="primary"
                    size="medium"
                    onClick={applyInsurance}
                >
                    Update
                </Button>
            </InsuranceFooterContainer>
        );

        return (
            <>
                <DiscardChangesModal
                    show={showDiscardModal}
                    onDiscard={handleDiscardChanges}
                    onGoBack={handleGoBack}
                    hasValidationErrors={!isFormValid}
                />
                {!embedded && <LoadingSection loading={updateStatus === 'sending'} opacity={1} />}
                <InsuranceForm
                    ref={formRef}
                    vehicleInsuranceInfo={vehicleInsuranceInfo}
                    isVehicleInsuranceUpdated={isVehicleInsuranceUpdated}
                    insuranceProviders={insuranceProviders}
                    getInsuranceProviders={getInsuranceProviders}
                    isLocked={isLocked}
                    isContractingContext={isContractingContext}
                    contractApiErroredVehicleInsurance={contractApiErroredVehicleInsurance}
                    isAdditionalInsuranceFieldsEnabled={isAdditionalInsuranceFieldsEnabled}
                    buyerState={buyerInfo?.state ?? null}
                    onChange={handleFormChange}
                    onContractingErrorsAlert={onContractingErrorsAlert}
                    onSetErrorFields={onSetErrorFields}
                    onProviderInteraction={handleProviderInteraction}
                    hostApp="MV"
                    logContext={logContext}
                />
                {!embedded && slideOutFooterActions}
            </>
        );
    }
);

InsuranceFormContainer.displayName = 'InsuranceFormContainer';

export default InsuranceFormContainer;
